import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { isEmpty } from 'validate.js';

import { ANALYTICS_EVENTS } from 'dpl/constants/analytics';
import { sendAnalyticsEvent } from 'dpl/util/analytics';
import Icon from 'dpl/common/components/Icon';
import TransitionMenu from 'dpl/common/components/TransitionMenu';
import Input from 'dpl/common/design_system/Input';
import { BREED_PROFILES, BREED_TYPES } from 'dpl/components/BreedSearch/util';
import BreedSearchResultsContainer from 'dpl/containers/BreedSearchResultsContainer';
import MobileLocationResultsListContainer from 'dpl/search/containers/MobileLocationResultsListContainer';
import MenuDrawer from 'dpl/search_result_filters/components/SearchResultsHeader/MenuDrawer';
import { navigateToShelterResults } from 'dpl/shelter_search_results/utils';

import {
  HEADER_SEARCH_TYPE_LABEL_VALUES,
  HEADER_SEARCH_TYPE_VALUES,
  HEADER_SEARCH_TYPES
} from './constants';

export default function SearchMenuDrawer({
  breedSearchAnalytics,
  shelterSearchAnalytics,
  onClose,
  onParentClose
}) {
  const inputRef = useRef(null);
  const [searchType, setSearchType] = useState(HEADER_SEARCH_TYPES.BREEDERS);
  const [query, setQuery] = useState('');

  const isBreedGuidesSearch = searchType === HEADER_SEARCH_TYPES.BREED_GUIDES;
  const isSheltersSearch = searchType === HEADER_SEARCH_TYPES.SHELTERS;
  const isBreedersSearch = searchType === HEADER_SEARCH_TYPES.BREEDERS;
  const breedType = isBreedGuidesSearch ? BREED_PROFILES : BREED_TYPES.ALL;

  function handleMenuItemClick(e, index) {
    e.preventDefault();
    setQuery('');
    setSearchType(HEADER_SEARCH_TYPE_VALUES[index]);
  }

  function handleShelterLocationClick(place) {
    if (shelterSearchAnalytics?.view) {
      sendAnalyticsEvent(ANALYTICS_EVENTS.SHELTER_SEARCHED, {
        view: shelterSearchAnalytics.view,
        view_feature: shelterSearchAnalytics.view_feature
      });
    }

    navigateToShelterResults(place);
    onClose();
    onParentClose?.();
  }

  function handleInputClear() {
    setQuery('');
    inputRef.current?.focus?.();
  }

  return (
    <MenuDrawer
      title="Search"
      classes={{
        modal: 'SearchMenuDrawerV2',
        header: 'bg-sand b--none',
        content: 'pv0'
      }}
      maxWidth="800px"
      onClose={onClose}
    >
      <div className="sticky bg-sand z-1" style={{ top: '64px' }}>
        <TransitionMenu
          className="mh5"
          items={HEADER_SEARCH_TYPE_LABEL_VALUES}
          selectedIdx={HEADER_SEARCH_TYPE_VALUES.indexOf(searchType)}
          onItemClick={handleMenuItemClick}
          itemClassName="font-16 fw-medium pb3"
          itemPadding={12}
          customColors={{
            itemColorClassName: 'tertiary',
            borderColorClassName: 'bg-default',
            selectedColorClassName: 'default-color'
          }}
        />
        <div className="bg-white pv4 ph5">
          <div className="relative">
            <Input
              ref={inputRef}
              type="text"
              name={isSheltersSearch ? 'location_search' : 'breed_search'}
              className={classnames('w-100', { ph10: isBreedersSearch })}
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={
                isSheltersSearch ? 'Add your location' : 'Search a breed'
              }
              aria-label={isSheltersSearch ? 'Shelter search' : 'Breed search'}
              autoFocus
            />
            {isBreedersSearch && (
              <Icon
                className="primary absolute transform-center"
                name="fetch-search"
                height="20px"
                width="20px"
                style={{ left: '12px' }}
              />
            )}
            {!isEmpty(query) && (
              <button
                type="button"
                className="absolute top-0 bottom-0"
                style={{ right: '12px' }}
                onClick={handleInputClear}
              >
                <Icon name="fetch-close" height="20px" width="20px" />
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="pb5">
        {(isBreedersSearch || isBreedGuidesSearch) && (
          <BreedSearchResultsContainer
            analytics={isBreedersSearch ? breedSearchAnalytics : {}}
            query={query}
            breedType={breedType}
            isMobile
            isOpen
          />
        )}
        {isSheltersSearch && (
          <MobileLocationResultsListContainer
            onSelectItem={handleShelterLocationClick}
            query={query}
            isNewLayout
          />
        )}
      </div>
    </MenuDrawer>
  );
}

SearchMenuDrawer.propTypes = {
  breedSearchAnalytics: PropTypes.shape({
    origin_view: PropTypes.string,
    origin_view_feature: PropTypes.string
  }),
  shelterSearchAnalytics: PropTypes.shape({
    view: PropTypes.string,
    view_feature: PropTypes.string
  }),
  onClose: PropTypes.func.isRequired,
  onParentClose: PropTypes.func
};

SearchMenuDrawer.defaultProps = {
  breedSearchAnalytics: {},
  shelterSearchAnalytics: {},
  onParentClose: null
};
